import { getCognitoAppName } from '@/shared/lib/cognitoAppName';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Mail } from 'lucide-react';
import { useResetPassword } from '../../hooks/useUsers';

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
}

/**
 * Confirmation dialog for triggering an email-based password reset via Cognito.
 *
 * No password is captured here — the backend calls AdminResetUserPasswordCommand
 * which causes Cognito to email the target user a temporary password. The user
 * is then forced through the NEW_PASSWORD_REQUIRED challenge on next login.
 */
export function ResetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userName,
}: ResetPasswordDialogProps) {
  const resetPassword = useResetPassword();

  const handleSubmit = async () => {
    // appName drives the Cognito CustomMessage Lambda template — without it
    // the Lambda falls through to Ignite Core defaults and ships a broken
    // logo. Same fix as the public forgot-password flow in authStore.
    await resetPassword.mutateAsync({
      userId,
      data: { mode: 'email', appName: getCognitoAppName() },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            {userName
              ? `Send a password reset email to ${userName}?`
              : 'Send a password reset email to this user?'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 text-sm text-muted-foreground space-y-2">
          <div className="flex items-start gap-2">
            <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              The user will receive an email from Cognito with a temporary password and will be
              required to set a new password the next time they sign in.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={resetPassword.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={resetPassword.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {resetPassword.isPending ? 'Sending…' : 'Send Reset Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
